import { lazy, Suspense } from "react";
import { motion } from "framer-motion";
import { Users, Trophy, BarChart3 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Capacitor } from "@capacitor/core";

const StrengthCalculator = lazy(() => import("@/components/StrengthCalculator"));
const StrengthStandards = lazy(() => import("@/components/StrengthStandards"));
import MobileNav from "@/components/MobileNav";

const Index = () => {
  const { t } = useTranslation();

  const isNative = Capacitor.isNativePlatform();

  const stats = [
    { icon: Users, label: t("hero.lifters"), value: "13M+" },
    { icon: BarChart3, label: t("hero.liftsLogged"), value: "153M+" },
    { icon: Trophy, label: t("hero.exercises"), value: "200+" },
  ];

  return (
    <main className="min-h-screen bg-background">
      <MobileNav />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="/hero-gym.jpg" alt="Gym atmosphere" className="w-full h-full object-cover opacity-30" fetchPriority="high" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/80 to-background" />
        </div>

        <div className="relative container px-4 py-12 sm:py-20 md:py-32 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-heading text-3xl sm:text-5xl md:text-7xl font-bold mb-3 md:mb-4 leading-tight"
          >
            {t("hero.title1")}{" "}
            <span className="text-primary text-glow">{t("hero.title2")}</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="text-sm sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8 md:mb-10 text-center px-2"
          >
            {t("hero.subtitle")}
          </motion.p>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="flex justify-center gap-6 sm:gap-8 md:gap-16"
          >
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <stat.icon className="w-4 h-4 sm:w-5 sm:h-5 text-primary mx-auto mb-1" />
                <p className="font-heading text-xl sm:text-2xl md:text-3xl font-bold">{stat.value}</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground">{stat.label}</p>
              </div>
            ))}
          </motion.div>

          {/* Store Badges - hidden in native apps */}
          {!isNative && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.45 }}
              className="flex justify-center gap-4 mt-8"
            >
              <a href="https://apps.apple.com/us/app/strength-calculator/id6759311426" target="_blank" rel="noopener noreferrer">
                <img src="/badge-appstore.svg" alt="Download on the App Store" className="h-10 sm:h-12" />
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.appilix.app1771004394&hl=eng" target="_blank" rel="noopener noreferrer">
                <img src="/badge-googleplay.svg" alt="Get it on Google Play" className="h-10 sm:h-12" />
              </a>
            </motion.div>
          )}

        </div>
      </section>

      {/* Calculator */}
      <Suspense fallback={<div className="container px-4 py-10 sm:py-16 md:py-24 min-h-[200px]" />}>
        <section id="calculator" className="container px-4 py-10 sm:py-16 md:py-24">
          <StrengthCalculator />
        </section>
      </Suspense>

      {/* Standards */}
      <Suspense fallback={<div className="container px-4 py-10 sm:py-16 md:py-24 min-h-[200px]" />}>
        <section id="standards" className="container px-4 py-10 sm:py-16 md:py-24 border-t border-border/50">
          <StrengthStandards />
        </section>
      </Suspense>

      {/* Footer */}
      <footer className="border-t border-border/50 py-6 md:py-8">
        <div className="container px-4 text-center text-xs sm:text-sm text-muted-foreground space-y-2">
          <p>{t("footer.text")}</p>
          <p>© {new Date().getFullYear()} Strength-Calculator. All rights reserved.</p>
          <p>
            <a href="/privacy-policy" className="text-primary hover:underline">Privacy Policy</a>
          </p>
        </div>
      </footer>
    </main>
  );
};

export default Index;
